import { CartProviderProps } from "app/providers/CartProvider";
import { OrderProviderProps } from "app/providers/OrderProvider";
import { useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import {
  CreateCartPayload,
  GetOrderResponse,
} from "shared/api/middleware-api-client";
import Icon, { IconTypes } from "shared/ui/icon";
import Text from "shared/ui/typography/Text";
import { concatClassnames } from "shared/utils/concatClassnames";
import { getFormattedPrice } from "shared/utils/getFormattedPrice";
import { useVoucher } from "app/hooks/shared/useVoucher";
import { useStandardProductDeliveryCostsCalculator } from "app/hooks/shared/standard-product/useStandardProductDeliveryCostsCalculator";
import {
  StandardProductReceiveMethod,
  useStandardProductReceiveMethod,
} from "app/hooks/shared/standard-product/useStandardProductReceiveMethod";
import { decryptVoucher } from "shared/utils/voucher";
import { ProductContext } from "shared/utils/productContext";
import { useVoucherCartAddition } from "app/hooks/shared/useVoucherCartAddition";
import { DELIVERY_PRICES } from "shared/config/pricing";
interface PriceTableProps {
  className?: string;
  entity: CreateCartPayload | GetOrderResponse; // entity is used because PriceTable can be used with cart or with order
  context: OrderProviderProps | CartProviderProps;
}

const StandardProductPriceTable = ({
  className,
  entity,
  context,
}: PriceTableProps) => {
  const { standardProductDeliveryCosts, standardProductAssemblyCosts } =
    useStandardProductDeliveryCostsCalculator(
      entity as CreateCartPayload,
      false,
    );
  const { addVoucher } = useVoucher();
  const { addVoucherToCart } = useVoucherCartAddition();
  const currentStandardProductReceiveMethod = useStandardProductReceiveMethod(
    entity as CreateCartPayload,
  );

  // Calculate the warranty package based on the entity and context
  const warrantyPackage = useMemo(() => {
    if (entity.warrantyPackage && entity.warrantyPackage.code !== "") {
      return entity.warrantyPackage;
    } else if (
      "warrantyPackage" in context &&
      context.warrantyPackage &&
      context.warrantyPackage.code !== ""
    ) {
      return context.warrantyPackage;
    }

    return entity.warrantyPackage;
  }, [entity, context]);

  // Calculate the discount records based on the entity and context
  const discountRecords = useMemo(() => {
    if (
      "discountRecords" in entity &&
      entity.discountRecords &&
      "getReadableDiscountRecordPrice" in context
    ) {
      return entity.discountRecords.map((record) => ({
        title: record.title,
        price: context.getReadableDiscountRecordPrice(record),
        id: record.id,
      }));
    }

    return [];
  }, [entity]);

  // This useEffect hook is responsible for adding a voucher to the cart if a discount code is present
  useEffect(() => {
    if ("cart" in context && context.cart.discountCode) {
      // Determine if its normal or standard cart based on the presence of products in the cart
      const productContext =
        context.cart.products.length > 0
          ? ProductContext.DoorContext
          : ProductContext.StandardProductContext;
      const decryptedVoucher = decryptVoucher(context.cart.discountCode);

      // Add the voucher to the cart using the addVoucherToCart function
      if (decryptedVoucher) {
        addVoucherToCart(
          decryptedVoucher, // Decrypt the discount code
          productContext, // Pass the product context
          true, // Set the flag to indicate that the voucher is being added because of a page syncronization, not a user action
        );
      }
    }
  }, []);

  const voucher = useMemo(() => {
    const voucherObj = {
      name: "",
      price: "",
    };
    if ("cart" in context && context.cart.discountCode) {
      if (
        "newVoucher" in context &&
        context.newVoucher &&
        "getVoucherDisplay" in context
      ) {
        voucherObj.name = context.getVoucherDisplay();
      }

      if (
        "newVoucher" in context &&
        context.newVoucher &&
        "getAbsoluteVoucherDiscount" in context
      ) {
        voucherObj.price = context.getAbsoluteVoucherDiscount();
      }
    }

    return voucherObj;
  }, [context]);

  // Function to handle the removal of a voucher
  const onRemoveVoucherClick = async () => {
    if ("removeVoucher" in context) {
      // If the context has a removeVoucher function (cart context), call it to remove the voucher
      context.removeVoucher();
      toast.success("Voucher is verwijderd"); // Show success message
    } else {
      try {
        // If the context does not have a removeVoucher function ( order context), call addVoucher with an empty string to remove the voucher
        await addVoucher("");
        toast.success("Voucher is verwijderd!"); // Show success message
      } catch (e: any) {
        toast.error("Kon de voucher niet verwijderen!"); // Show error message if removing the voucher fails
      }
    }
  };

  const showWarranty = useMemo(() => {
    return (
      warrantyPackage !== undefined &&
      warrantyPackage.packageId !== "no_package_now" &&
      warrantyPackage.packageId !== "decide_later"
    );
  }, [warrantyPackage]);

  const selfAssemblyDeliveryCosts = useMemo(() => {
    if (
      "containsZakelijkProducts" in context &&
      context.containsZakelijkProducts
    ) {
      return DELIVERY_PRICES.ZAKELIJK;
    }

    if (entity.isSelfAssembly) {
      return DELIVERY_PRICES.SELF_ASSEMBLY;
    }
    return 0;
  }, [entity]);

  const totalExtraDiscount =
    "getAllExtraDiscounts" in context ? context.getAllExtraDiscounts() : 0;
  const showTotalExtraDiscount = totalExtraDiscount > 0;

  const totalZakelijkDiscount =
    "getAllZakelijkDiscounts" in context
      ? context.getAllZakelijkDiscounts()
      : 0;
  const showZakelijkDiscounts = totalZakelijkDiscount > 0;
  return (
    <div
      className={concatClassnames(
        "gg-grid gg-grid-cols-12 lg:gg-flex lg:gg-gap-x-11",
        className,
      )}
    >
      <div className="gg-col-span-9 gg-grid gg-gap-y-2.5 sm:gg-col-span-6">
        <Text>Subtotaal</Text>

        {standardProductDeliveryCosts > 0 &&
          currentStandardProductReceiveMethod ===
            StandardProductReceiveMethod.DELIVERY && <Text>Verzendkosten</Text>}

        {entity.isSelfAssembly && <Text>Bezorgkosten</Text>}

        {standardProductAssemblyCosts > 0 &&
          currentStandardProductReceiveMethod ===
            StandardProductReceiveMethod.DELIVERYWITHASSEMBLY && (
            <Text>Montagekosten</Text>
          )}

        {discountRecords.length
          ? discountRecords.map((record) => (
              <Text
                key={`discount-record-${record.title}-title`}
                className="gg-flex gg-items-center gg-gap-x-3"
              >
                <button
                  onClick={() => onRemoveVoucherClick()}
                  data-testid="remove-voucher-button"
                  className={record.id === "extraDoors" ? "gg-hidden" : ""}
                >
                  <Icon name={IconTypes.REMOVE} />
                </button>
                {record.title}
              </Text>
            ))
          : showTotalExtraDiscount &&
            !showZakelijkDiscounts && <Text>Korting extra product</Text>}

        {showZakelijkDiscounts && <Text>Korting zakelijk </Text>}

        {showWarranty && <Text>{warrantyPackage!.name.split("-")[0]}</Text>}

        {voucher.name && (
          <div className="gg-flex gg-gap-x-3">
            <Text>Voucher: {voucher.name}</Text>

            <button
              onClick={() => onRemoveVoucherClick()}
              data-testid="remove-voucher-button"
            >
              <Icon name={IconTypes.REMOVE} />
            </button>
          </div>
        )}
      </div>
      <div className="gg-col-span-3 gg-grid gg-gap-y-2.5 gg-justify-self-end gg-text-right sm:gg-col-span-6 sm:gg-col-start-7 lg:gg-pl-0 lg:gg-text-right">
        <Text data-testid="subtotal-price" bold>
          {context.getFormattedSubTotalPrice()}
        </Text>

        {standardProductDeliveryCosts > 0 &&
          currentStandardProductReceiveMethod ===
            StandardProductReceiveMethod.DELIVERY && (
            <Text data-testid="standard-product-delivery-cost" bold>
              {getFormattedPrice(standardProductDeliveryCosts)}
            </Text>
          )}
        {entity.isSelfAssembly && (
          <Text bold>{getFormattedPrice(selfAssemblyDeliveryCosts)}</Text>
        )}
        {standardProductAssemblyCosts > 0 &&
          currentStandardProductReceiveMethod ===
            StandardProductReceiveMethod.DELIVERYWITHASSEMBLY && (
            <Text data-testid="standard-product-assembly-cost" bold>
              {getFormattedPrice(standardProductAssemblyCosts)}
            </Text>
          )}

        {discountRecords.length
          ? discountRecords.map((record) => (
              <Text key={`discount-record-${record.title}-price`} bold>
                {record.price}
              </Text>
            ))
          : showTotalExtraDiscount &&
            !showZakelijkDiscounts && (
              <Text bold className="gg-text-green-300">
                - {getFormattedPrice(totalExtraDiscount)}
              </Text>
            )}

        {showZakelijkDiscounts && (
          <Text bold className="gg-text-green-300">
            - {getFormattedPrice(totalZakelijkDiscount)}
          </Text>
        )}

        {showWarranty && (
          <Text bold>{getFormattedPrice(warrantyPackage?.price)}</Text>
        )}

        {voucher.price ? <Text bold>{voucher.price}</Text> : <></>}
      </div>
    </div>
  );
};
export default StandardProductPriceTable;
